/**
 * Beneficiary management — list, create, and update bank details.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Beneficiary } from '../shared/api';
import {
  Empty, ErrorNote, Modal, PlusIcon, Skeleton, titleise,
  useDebounced, useToast,
} from '../shared/ui';

const KINDS = ['EMPLOYEE', 'VENDOR'] as const;

export function BeneficiariesPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const debounced = useDebounced(search, 300);
  const [editBen, setEditBen] = useState<Beneficiary | null>(null);

  const qs = new URLSearchParams({ limit: '500' });
  if (debounced) qs.set('q', debounced);
  if (kindFilter) qs.set('kind', kindFilter);

  const { data, isLoading, error } = useQuery({
    queryKey: ['beneficiaries', qs.toString()],
    queryFn: () => api.get<Beneficiary[]>(`/reference/beneficiaries?${qs}`),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Reference</div>
          <h1 className="page-title">Beneficiaries</h1>
          <p className="page-sub">
            {data ? `${data.length} beneficiar${data.length === 1 ? 'y' : 'ies'}` : 'Loading…'}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <PlusIcon /> Add beneficiary
        </button>
      </div>

      <div className="filter-bar card row row-wrap gap-10" style={{ marginBottom: 14, padding: 13 }}>
        <input
          className="input grow filter-search"
          placeholder="Search by name or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search beneficiaries"
        />
        <select
          className="select filter-select" value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          aria-label="Filter by kind"
        >
          <option value="">All types</option>
          {KINDS.map((k) => <option key={k} value={k}>{titleise(k)}</option>)}
        </select>
        {(search || kindFilter) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setKindFilter(''); }}>
            Clear
          </button>
        )}
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? <Skeleton rows={8} /> : !data || data.length === 0 ? (
        <div className="card">
          <Empty
            title="No beneficiaries"
            hint={search || kindFilter
              ? 'No beneficiaries match these filters.'
              : 'Add employees and vendors to use them in payment orders and payroll.'}
            action={
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                Add beneficiary
              </button>
            }
          />
        </div>
      ) : (
        <div className="card card-flush">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th className="hide-mobile">Department</th>
                  <th className="hide-mobile">Category</th>
                  <th className="hide-mobile">Account</th>
                  <th className="hide-mobile">UPI</th>
                  <th className="hide-mobile">Bank changed</th>
                </tr>
              </thead>
              <tbody>
                {data.map((b) => (
                  <tr
                    key={b.id}
                    className="clickable"
                    onClick={() => setEditBen(b)}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditBen(b); }}
                  >
                    <td>
                      <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{b.code}</span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{b.name}</td>
                    <td>
                      <span className={`pill ${b.kind === 'EMPLOYEE' ? 'pill-info' : 'pill-draft'}`}>
                        {titleise(b.kind)}
                      </span>
                    </td>
                    <td className="hide-mobile faint">{b.department ?? '—'}</td>
                    <td className="hide-mobile faint">{b.category ?? '—'}</td>
                    <td className="hide-mobile mono faint" style={{ fontSize: 12 }}>
                      {b.account_last4 ? `····${b.account_last4}` : '—'}
                    </td>
                    <td className="hide-mobile faint" style={{ fontSize: 12 }}>{b.upi_id ?? '—'}</td>
                    <td className="hide-mobile ghost" style={{ fontSize: 11 }}>
                      {b.bank_detail_changed_at
                        ? new Date(b.bank_detail_changed_at).toLocaleDateString('en-IN')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <CreateBeneficiary onClose={() => setShowCreate(false)} />}
      {editBen && <EditBankDetails ben={editBen} onClose={() => setEditBen(null)} />}
    </>
  );
}


/* ── Create beneficiary ───────────────────────────────────────────────── */

function CreateBeneficiary({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    code: '', name: '',
    kind: 'EMPLOYEE' as typeof KINDS[number],
    department: '', category: '',
    account_last4: '', ifsc: '', upi_id: '',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const create = useMutation({
    mutationFn: () => api.post<Beneficiary>('/reference/beneficiaries', {
      ...form,
      department: form.department || null,
      category: form.category || null,
      account_last4: form.account_last4 || null,
      ifsc: form.ifsc || null,
      upi_id: form.upi_id || null,
    }),
    onSuccess: (ben) => {
      qc.invalidateQueries({ queryKey: ['beneficiaries'] });
      toast(`Beneficiary "${ben.name}" created`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to create beneficiary', 'bad'),
  });

  const valid = form.code && form.name;

  return (
    <Modal title="Add beneficiary" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
          <div className="field">
            <label className="label">Code</label>
            <input className="input" placeholder="EMP001 or VND001" value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())} />
          </div>
          <div className="field">
            <label className="label">Name</label>
            <input className="input" placeholder="Full name or company" value={form.name}
              onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Type</label>
            <select className="select" value={form.kind} onChange={(e) => set('kind', e.target.value)}>
              {KINDS.map((k) => <option key={k} value={k}>{titleise(k)}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Department</label>
            <input className="input" placeholder="Engineering" value={form.department}
              onChange={(e) => set('department', e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Category</label>
            <input className="input" placeholder="Consultant, Supplier…" value={form.category}
              onChange={(e) => set('category', e.target.value)} />
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <div className="label" style={{ marginBottom: 10 }}>Bank details (optional)</div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14 }}>
            <div className="field">
              <label className="label">Account (last 4)</label>
              <input className="input" placeholder="1234" maxLength={4} inputMode="numeric"
                value={form.account_last4} onChange={(e) => set('account_last4', e.target.value)} />
            </div>
            <div className="field">
              <label className="label">IFSC</label>
              <input className="input" placeholder="HDFC0001234" maxLength={11}
                value={form.ifsc} onChange={(e) => set('ifsc', e.target.value.toUpperCase())} />
            </div>
            <div className="field">
              <label className="label">UPI ID</label>
              <input className="input" placeholder="name@upi" value={form.upi_id}
                onChange={(e) => set('upi_id', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!valid || create.isPending}
              onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Add beneficiary'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ── Edit bank details ────────────────────────────────────────────────── */

function EditBankDetails({ ben, onClose }: { ben: Beneficiary; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    account_last4: ben.account_last4 ?? '',
    ifsc: '',
    upi_id: ben.upi_id ?? '',
  });

  const set = (field: string, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const update = useMutation({
    mutationFn: () => api.patch<Beneficiary>(`/reference/beneficiaries/${ben.id}/bank-details`, {
      account_last4: form.account_last4 || null,
      ifsc: form.ifsc || null,
      upi_id: form.upi_id || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['beneficiaries'] });
      toast(`Bank details updated for ${ben.name}`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Failed to update bank details', 'bad'),
  });

  return (
    <Modal title={`${ben.name} — Bank details`} onClose={onClose}>
      <div className="col gap-14">
        <div className="row between" style={{ fontSize: 13 }}>
          <span className="faint">Code</span>
          <span className="mono" style={{ fontWeight: 600 }}>{ben.code}</span>
        </div>
        <div className="row between" style={{ fontSize: 13 }}>
          <span className="faint">Type</span>
          <span className={`pill ${ben.kind === 'EMPLOYEE' ? 'pill-info' : 'pill-draft'}`}>{titleise(ben.kind)}</span>
        </div>
        {ben.department && (
          <div className="row between" style={{ fontSize: 13 }}>
            <span className="faint">Department</span>
            <span>{ben.department}</span>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <div className="hint" style={{ fontSize: 12, marginBottom: 14 }}>
            Changing bank details is tracked by the risk engine. Payments to accounts changed recently will be flagged.
          </div>
          <div className="col gap-10">
            <div className="field">
              <label className="label">Account (last 4 digits)</label>
              <input className="input" placeholder="1234" maxLength={4} inputMode="numeric"
                value={form.account_last4} onChange={(e) => set('account_last4', e.target.value)} />
            </div>
            <div className="field">
              <label className="label">IFSC code</label>
              <input className="input" placeholder="HDFC0001234" maxLength={11}
                value={form.ifsc} onChange={(e) => set('ifsc', e.target.value.toUpperCase())} />
            </div>
            <div className="field">
              <label className="label">UPI ID</label>
              <input className="input" placeholder="name@upi"
                value={form.upi_id} onChange={(e) => set('upi_id', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div />
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={update.isPending}
              onClick={() => update.mutate()}>
              {update.isPending ? 'Saving…' : 'Update bank details'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
